import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';

/**
 * 6-digit SMS code field: one real input (so iOS/Android SMS suggestions, paste and WebOTP work)
 * drawn as six boxes. Emits codeComplete once all digits are in.
 */
@Component({
  selector: 'app-otp-code-input',
  templateUrl: './otp-code-input.component.html',
  styleUrls: ['./otp-code-input.component.css'],
})
export class OtpCodeInputComponent implements AfterViewInit, OnDestroy {
  @Input() disabled = false;
  @Input() hasError = false;
  @Input() isDarkMode = false;
  @Output() codeComplete = new EventEmitter<string>();
  @ViewChild('codeInput') codeInput: ElementRef<HTMLInputElement>;

  readonly length = 6;
  code = '';
  isFocused = false;
  private webOtpAbort: AbortController | null = null;
  private focusTimer: ReturnType<typeof setTimeout> | null = null;

  get slots(): string[] {
    return Array.from({ length: this.length }, (_, i) => this.code[i] || '');
  }

  get activeIndex(): number {
    return Math.min(this.code.length, this.length - 1);
  }

  /** Red boxes only until the user starts typing the next attempt. */
  get showError(): boolean {
    return this.hasError && this.code.length === 0;
  }

  ngAfterViewInit(): void {
    this.focusTimer = setTimeout(() => this.focus(), 200);
    this.listenForWebOtp();
  }

  ngOnDestroy(): void {
    if (this.focusTimer) {
      clearTimeout(this.focusTimer);
    }
    this.webOtpAbort?.abort();
  }

  onInput(value: string): void {
    this.code = String(value || '').replace(/\D/g, '').slice(0, this.length);
    if (this.codeInput) {
      this.codeInput.nativeElement.value = this.code;
    }
    if (this.code.length === this.length) {
      this.codeComplete.emit(this.code);
    }
  }

  clear(): void {
    this.onInput('');
    // Callers clear right after a failed verify, while [disabled] is still true; focus once it re-enables.
    if (this.focusTimer) {
      clearTimeout(this.focusTimer);
    }
    this.focusTimer = setTimeout(() => this.focus(), 0);
  }

  focus(): void {
    this.codeInput?.nativeElement.focus();
  }

  /** Chrome on Android reads the code from the SMS (its last line is "@print.eazix.io #123456"). */
  private listenForWebOtp(): void {
    if (typeof window === 'undefined' || !('OTPCredential' in window)) {
      return;
    }
    this.webOtpAbort = new AbortController();
    (navigator.credentials as any)
      .get({ otp: { transport: ['sms'] }, signal: this.webOtpAbort.signal })
      .then((otp: { code?: string } | null) => {
        if (otp?.code && !this.disabled) {
          this.onInput(otp.code);
        }
      })
      .catch(() => {
        /* aborted, denied, or the SMS did not match this origin */
      });
  }
}
